import { useEffect, useState, useMemo } from "react";
import useAxiosInstance from "../../../../../Utilities/Hooks/useAxiosInstance";
import "./inventory-modals.css";

const LOC_OPTIONS = [
  { value: "mainOffice", label: "Main Office" },
  { value: "showRoom",   label: "Showroom"   },
  { value: "wareHouse",  label: "Warehouse"  },
];

export default function TransferStockModal({ productSizeId, onClose, onSuccess }) {
  const axios = useAxiosInstance();

  const [loading, setLoading] = useState(false);
  const [counts, setCounts]   = useState({ total: 0, byLocation: {} });

  const [fromLoc, setFromLoc] = useState("");
  const [toLoc,   setToLoc]   = useState("");
  const [qty,     setQty]     = useState("");

  const [error, setError]     = useState("");
  const [okMsg, setOkMsg]     = useState("");

  // fetch live counts
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `https://api.stridesco.com/api/employee/inventory/stockUnits/counts/by-size/${productSizeId}`
        );
        if (!off) setCounts(data || { total: 0, byLocation: {} });
      } catch {
        if (!off) setCounts({ total: 0, byLocation: {} });
      }
    })();
    return () => { off = true; };
  }, [axios, productSizeId]);

  const availableFrom = useMemo(() => {
    return (fromLoc && counts?.byLocation?.[fromLoc]) ? counts.byLocation[fromLoc] : 0;
  }, [counts, fromLoc]);

  const disableSubmit = useMemo(() => {
    const n = Number(qty);
    return (
      loading ||
      !fromLoc ||
      !toLoc ||
      fromLoc === toLoc ||
      !n ||
      n <= 0 ||
      n > availableFrom
    );
  }, [loading, fromLoc, toLoc, qty, availableFrom]);

  function resetMessages() {
    setError(""); setOkMsg("");
  }

  async function submitTransfer(e) {
    e.preventDefault();
    resetMessages();

    const nqty = Number(qty);
    if (!fromLoc || !toLoc) return setError("Pick both source and destination.");
    if (fromLoc === toLoc)  return setError("Source and destination cannot be the same.");
    if (!nqty || nqty <= 0) return setError("Quantity must be a positive number.");
    if (nqty > availableFrom) {
      return setError(`Only ${availableFrom} available at ${LOC_OPTIONS.find(l => l.value === fromLoc)?.label}.`);
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `https://api.stridesco.com/api/employee/inventory/stockUnits/transfer`,
        { productSizeId, from: fromLoc, to: toLoc, qty: nqty }
      );

      setOkMsg(`Moved ${data?.moved ?? nqty} units.`);
      setQty("");

      // refresh counts after move
      const { data: fresh } = await axios.get(
        `https://api.stridesco.com/api/employee/inventory/stockUnits/counts/by-size/${productSizeId}`
      );
      setCounts(fresh || { total: 0, byLocation: {} });

      // let parent update the row immediately
      onSuccess?.(fresh || { total: 0, byLocation: {} });
    } catch (err) {
      setError(err?.response?.data?.error || "Transfer failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="inv-modal">
      <div className="inv-modal__title">Transfer Stock</div>

      <div className="inv-modal__section">
        <div className="inv-stats">
          <div className="inv-stat">
            <div className="inv-stat__label">Main Office</div>
            <div className="inv-stat__value">{counts?.byLocation?.mainOffice ?? 0}</div>
          </div>
          <div className="inv-stat">
            <div className="inv-stat__label">Showroom</div>
            <div className="inv-stat__value">{counts?.byLocation?.showRoom ?? 0}</div>
          </div>
          <div className="inv-stat">
            <div className="inv-stat__label">Warehouse</div>
            <div className="inv-stat__value">{counts?.byLocation?.wareHouse ?? 0}</div>
          </div>
          <div className="inv-stat inv-stat--total">
            <div className="inv-stat__label">Total</div>
            <div className="inv-stat__value">{counts?.total ?? 0}</div>
          </div>
        </div>
      </div>

      <form onSubmit={submitTransfer} className="inv-form">
        <div className="inv-row">
          <div className="inv-col">
            <label className="inv-label">From</label>
            <select
              className="inv-select"
              value={fromLoc}
              onChange={(e) => { setFromLoc(e.target.value); resetMessages(); }}
            >
              <option value="">Select source</option>
              {LOC_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="inv-help">Available: {availableFrom}</div>
          </div>

          <div className="inv-col">
            <label className="inv-label">To</label>
            <select
              className="inv-select"
              value={toLoc}
              onChange={(e) => { setToLoc(e.target.value); resetMessages(); }}
            >
              <option value="">Select destination</option>
              {LOC_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="inv-col">
            <label className="inv-label">Quantity</label>
            <input
              type="number"
              min="1"
              max={availableFrom || 0}
              className="inv-input"
              value={qty}
              onChange={(e) => { setQty(e.target.value); resetMessages(); }}
              placeholder="0"
            />
          </div>
        </div>

        {error && <div className="inv-alert inv-alert--error">{error}</div>}
        {okMsg && <div className="inv-alert inv-alert--ok">{okMsg}</div>}

        <div className="inv-actions">
          <button type="button" className="inv-btn inv-btn--ghost" onClick={onClose}>Close</button>
          <button type="submit" className="inv-btn inv-btn--primary" disabled={disableSubmit}>
            {loading ? "Transferring…" : "Transfer"}
          </button>
        </div>
      </form>
    </div>
  );
}
